import type { FieldWarning } from "@/game_profiles/types";

/**
 * Validation for the Core Keeper game ID, mirroring `GeneratedGameIdField` in
 * `decline/game_profiles/core_keeper.py`. Both are tested against
 * `tests/unit/game_profiles/core_keeper/game_id_cases.json`.
 *
 * The backend replaces an invalid game ID with a generated one on save, just as the game would. The
 * panel warns about it while the customer types, so the replacement does not come as a surprise.
 * An empty field is not a problem: it is how a customer asks for a generated ID. Whitespace around
 * the ID is removed on save, so it is ignored here too.
 */

export const GAME_ID_MIN_LENGTH = 15;
export const GAME_ID_MAX_LENGTH = 28;
const ALLOWED_CHARACTER = /^[A-Za-z0-9]$/;
const EXCLUDED_CHARACTERS = new Set(["Y", "y", "x", "0", "O"]);
const INVISIBLE_CHARACTER = /^[\s\p{C}]$/u;

export type GameIdProblem = { kind: "too_short" } | { kind: "too_long" } | { kind: "characters"; characters: string[] };

/** Returns what is wrong with a game ID, or `null` when Core Keeper accepts it or it is empty. */
export function getGameIdProblem(value: unknown): GameIdProblem | null {
  if (typeof value !== "string") return null;
  const gameId = value.trim();
  if (gameId === "") return null;
  const characters = [...new Set(gameId)].filter((c) => !ALLOWED_CHARACTER.test(c) || EXCLUDED_CHARACTERS.has(c));
  if (characters.length > 0) return { kind: "characters", characters };
  if (gameId.length < GAME_ID_MIN_LENGTH) return { kind: "too_short" };
  if (gameId.length > GAME_ID_MAX_LENGTH) return { kind: "too_long" };
  return null;
}

/** Lists characters for a message, naming a space `␣` and other invisible characters by code point. */
export function describeCharacters(characters: string[]): string {
  return characters
    .map((c) => {
      if (c === " ") return "␣";
      if (!INVISIBLE_CHARACTER.test(c)) return c;
      return `U+${c.codePointAt(0).toString(16).toUpperCase().padStart(4, "0")}`;
    })
    .join(" ");
}

/** `fieldWarnings` check for the game ID field. */
export function getGameIdWarning(value: unknown): FieldWarning | null {
  const problem = getGameIdProblem(value);
  if (!problem) return null;
  const characters = problem.kind === "characters" ? describeCharacters(problem.characters) : "";
  return { key: `ngp/coreKeeper/gameid/${problem.kind}`, params: { characters } };
}
